
import { Json } from "@/integrations/supabase/types";
import { getAddressFromCoordinates } from "@/services/locationService";

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
  address?: string;
}

export interface BreakEntry {
  startTime: string;
  endTime?: string;
  duration?: number;
}

export const parseCoordinates = (coords: Json | undefined): LocationCoordinates | null => {
  if (!coords || typeof coords !== 'object' || coords === null) return null;
  
  const coordObj = coords as any;
  if (typeof coordObj.latitude === 'number' && typeof coordObj.longitude === 'number') {
    return {
      latitude: coordObj.latitude,
      longitude: coordObj.longitude,
      accuracy: coordObj.accuracy || 0,
      address: coordObj.address
    };
  }
  return null;
};

export const parseBreaks = (breaks: Json | undefined): BreakEntry[] => {
  if (!breaks || !Array.isArray(breaks)) return [];
  
  return breaks.map((breakItem: any) => ({
    startTime: breakItem.startTime || '',
    endTime: breakItem.endTime,
    duration: breakItem.duration
  }));
};

export const formatDuration = (startTime: string, endTime?: string) => {
  if (!endTime) return "En cours...";
  
  const start = new Date(`2000-01-01T${startTime}`);
  const end = new Date(`2000-01-01T${endTime}`);
  const diff = end.getTime() - start.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${hours}h ${minutes}m`;
};

export const formatLocation = async (coords: Json | undefined): Promise<string> => {
  const parsedCoords = parseCoordinates(coords);
  if (!parsedCoords) return "Non disponible";
  
  // Si l'adresse est déjà stockée, l'utiliser
  if (parsedCoords.address) {
    // Extraire la partie ville/commune de l'adresse
    const addressParts = parsedCoords.address.split(', ');
    
    // Chercher la partie avec le code postal et la ville
    const cityPart = addressParts.find(part => /\d{5}\s+/.test(part));
    if (cityPart) {
      return cityPart.trim();
    }
    
    // Sinon, prendre les 2 dernières parties (généralement ville, pays)
    if (addressParts.length >= 2) {
      return addressParts.slice(-2).join(', ');
    }
    
    return parsedCoords.address;
  }
  
  // Si pas d'adresse stockée, faire un appel à l'API de géolocalisation
  try {
    const address = await getAddressFromCoordinates(parsedCoords.latitude, parsedCoords.longitude);
    
    // Extraire la partie ville de l'adresse obtenue
    const addressParts = address.split(', ');
    const cityPart = addressParts.find(part => /\d{5}\s+/.test(part));
    if (cityPart) {
      return cityPart.trim();
    }
    
    // Sinon, prendre les 2 dernières parties
    if (addressParts.length >= 2) {
      return addressParts.slice(-2).join(', ');
    }
    
    return address;
  } catch (error) {
    console.error("Erreur lors de la récupération de l'adresse:", error);
    return `${parsedCoords.latitude.toFixed(6)}, ${parsedCoords.longitude.toFixed(6)}`;
  }
};

// Version synchrone pour l'affichage immédiat avec fallback
export const formatLocationSync = (coords: Json | undefined): string => {
  const parsedCoords = parseCoordinates(coords);
  if (!parsedCoords) return "Non disponible";
  
  if (parsedCoords.address) {
    const addressParts = parsedCoords.address.split(', ');
    
    const cityPart = addressParts.find(part => /\d{5}\s+/.test(part));
    if (cityPart) {
      return cityPart.trim();
    }
    
    if (addressParts.length >= 2) {
      return addressParts.slice(-2).join(', ');
    }
    
    return parsedCoords.address;
  }
  
  return `${parsedCoords.latitude.toFixed(6)}, ${parsedCoords.longitude.toFixed(6)}`;
};
